import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Grid, IconButton, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import React from "react";

import { FireDepartmentDisplay, LepcDisplay } from "./AgencyDisplay";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

export function SupplementalAgencies() {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();

  const lepcs = data?.tierIIReport.facility.supplementalLepcs ?? [];
  const fireDepartments = data?.tierIIReport.facility.supplementalFireDepartments ?? [];

  // Create tuples of [LEPC, FireDepartment] or [null, FireDepartment] or [LEPC, null]
  const agencyPairs: Array<[(typeof lepcs)[0] | null, (typeof fireDepartments)[0] | null]> = [];

  const maxLength = Math.max(lepcs.length, fireDepartments.length);

  for (let i = 0; i < maxLength; i++) {
    const lepc = i < lepcs.length ? lepcs[i] : null;
    const fireDepartment = i < fireDepartments.length ? fireDepartments[i] : null;
    agencyPairs.push([lepc, fireDepartment]);
  }

  // Determine if the associated facility is in California
  const isCA = data?.tierIIReport.facility.state === "CA";

  // Dynamic label for LEPC/CUPA
  const lepcLabel = isCA ? "CUPA" : "LEPC";

  // State for controlling main collapse
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-supplemental-agencies-expanded",
    { defaultValue: true }
  );

  if (!lepcs?.length && !fireDepartments?.length) {
    return null;
  }

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 2, 3)}
      sx={{ width: "100%" }}
    >
      {/* Main Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6">Supplemental Agencies</Typography>
        <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Grid container spacing={3}>
        {agencyPairs.map(([lepc, fireDepartment], i) => (
          <React.Fragment key={i}>
            {lepc && <LepcDisplay lepc={lepc.lepc} lepcLabel={lepcLabel} isExpanded={isExpanded} />}
            {fireDepartment && (
              <FireDepartmentDisplay
                fireDepartment={fireDepartment.fireDepartment}
                isExpanded={isExpanded}
              />
            )}
          </React.Fragment>
        ))}
      </Grid>
    </Stack>
  );
}
